<script setup lang="ts">
import { useGetStorageInfo } from "@/composable/useStorageQuota";
import { ref } from "vue";
import DRBtn from "../UI/DRBtn.vue";
import ConfirmationModal from "../generic/ConfirmationModal.vue";
import { storeToRefs } from "pinia";
import { useDeviceState } from "@/stores/DeviceState";
import { db } from "@/repositories/database";

const deviceState = useDeviceState();
const { isOnline } = storeToRefs(deviceState);

const {
  percentageUsed,
  percentageLimitLevel,
  countLimitLevel,
  localDrawingsCount,
} = useGetStorageInfo();

const progressColors = {
  low: "#28a745",
  medium: "#ffc107",
  high: "#dc3545",
};

const countColors = {
  low: undefined,
  medium: "#ffc107",
  high: "#dc3545",
};

const showConfirmFreeUpSpace = ref(false);

const confirmFreeUpSpace = async () => {
  await db.wipeDatabase();
  showConfirmFreeUpSpace.value = false;
};

const cancelFreeUpSpace = () => {
  showConfirmFreeUpSpace.value = false;
};
</script>

<template>
  <div>
    <div class="mb-1 d-flex align-items-center justify-content-between gap-1">
      <div class="d-flex align-items-center gap-2">
        <el-icon size="1.5rem"> <i class="bi-hdd"></i> </el-icon>
        <small>{{ $t("dashboard:settings.storage.title") }}</small>
      </div>
      <span class="d-none d-sm-none d-md-block fw-bold"
        >{{ percentageUsed.toFixed(2) }}%</span
      >
    </div>

    <div
      class="progress w-100"
      style="height: 7px"
      role="progressbar"
      :aria-valuenow="percentageUsed"
      aria-valuemin="0"
      aria-valuemax="100"
    >
      <div
        class="progress-bar progress-bar-striped"
        :style="{
          width: `${percentageUsed}%`,
          background: progressColors[percentageLimitLevel],
        }"
      ></div>
    </div>
  </div>

  <div class="mt-4">
    <div class="mb-1 d-flex align-items-center justify-content-between gap-1">
      <div class="d-flex align-items-center gap-2">
        <el-icon size="1.2rem"> <i class="bi-files"></i> </el-icon>
        <small>{{ $t("dashboard:settings.storage.localDrawingsCount") }}</small>
      </div>
      <span
        class="d- none d-sm-none d-md-block fw-bold"
        :style="{
          color: countColors[countLimitLevel],
        }"
        >{{ localDrawingsCount }}
        <el-icon v-if="countLimitLevel === 'high'" size="1rem"
          ><i class="bi-exclamation-triangle-fill text-danger"></i></el-icon
      ></span>
    </div>
  </div>

  <template v-if="isOnline"
    ><DRBtn
      class="mt-4 w-100"
      :label="$t('dashboard:settings.storage.freeUpSpace')"
      variant="outline-danger"
      @click="showConfirmFreeUpSpace = true"
    ></DRBtn>
    <ConfirmationModal
      v-model="showConfirmFreeUpSpace"
      @confirm="confirmFreeUpSpace"
      @cancel="cancelFreeUpSpace"
      :message="`${$t(
        'dashboard:settings.storage.freeUpSpaceConfirmMessage',
      )}`"
  /></template>
</template>

<style lang="scss" scoped></style>
